import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';

/** The source connector types that support a configurable extraction page size. */
export type PageSizeSourceType = 'Dataverse' | 'BusinessCentral' | 'Salesforce';

/** Inclusive bounds and the default for a connector's extraction page size. */
export interface PageSizeBounds {
  min: number;
  max: number;
  default: number;
}

/**
 * Returns the page-size bounds and default for a connector. Mirrors the backend validation ranges
 * (see #407): Dataverse 2000–5000, Business Central 2000–10000, Salesforce 1000–2000.
 * @param sourceType The source connector type.
 */
export function pageSizeBounds(sourceType: PageSizeSourceType): PageSizeBounds {
  switch (sourceType) {
    case 'BusinessCentral':
      return { min: 2000, max: 10000, default: 5000 };
    case 'Salesforce':
      return { min: 1000, max: 2000, default: 2000 };
    case 'Dataverse':
    default:
      return { min: 2000, max: 5000, default: 5000 };
  }
}

/**
 * Returns whether a page size is within the connector's allowed range.
 * @param sourceType The source connector type.
 * @param value The page size to check.
 */
export function isPageSizeValid(sourceType: PageSizeSourceType, value: number): boolean {
  const { min, max } = pageSizeBounds(sourceType);
  return Number.isFinite(value) && value >= min && value <= max;
}

/**
 * Props for {@link SourcePageSizeField}.
 */
interface SourcePageSizeFieldProps {
  /** The source connector type, which determines the allowed range. */
  sourceType: PageSizeSourceType;
  /** The current page size. */
  value: number;
  /** Called with the next page size on change. */
  onChange: (value: number) => void;
  /** Whether the input is disabled. */
  disabled?: boolean;
  /** DOM id for the input (defaults to a stable id). */
  id?: string;
}

/**
 * A single numeric input for a source's extraction page size, bounded to the connector's range.
 * Shared by the creation wizard's source step and the post-creation edit-source dialog so the
 * two surfaces use identical bounds and copy.
 */
export function SourcePageSizeField({
  sourceType,
  value,
  onChange,
  disabled = false,
  id = 'sourcePageSize',
}: SourcePageSizeFieldProps) {
  const { t } = useLanguage();
  const { min, max } = pageSizeBounds(sourceType);

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{t.sources.pageSizeField.label}</Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        disabled={disabled}
        value={value}
        onChange={(e) => { onChange(Number(e.target.value)); }}
      />
      <p className="text-xs text-muted-foreground">
        {t.sources.pageSizeField.description
          .replace('{min}', String(min))
          .replace('{max}', String(max))}
      </p>
    </div>
  );
}
